import { useEffect, useState, useCallback } from 'react';
import { apiFetch, getToken, STREAM_API } from '../utils/streamApi';
import MediaLightbox, { popupLink } from '../components/MediaLightbox';

// Device-Trigger Alerts report — only alerts that actually fired a device
// (siren / light / relay), with date + camera filters and image/clip proof.
// Available on both home and industrial.

const ymd = d => d.toISOString().slice(0, 10);
const toStart = s => new Date(`${s}T00:00:00`).getTime() / 1000;
const toEnd   = s => new Date(`${s}T23:59:59`).getTime() / 1000;
const fmt = ts => { try { return new Date(ts * 1000).toLocaleString(); } catch { return '—'; } };

// Why no device fired. Anything unmapped (e.g. "nothing_fired: <audio reason>") is
// shown verbatim; hover the cell for the full recorded detail.
const REASON_LABEL = {
  devices_paused:          'devices paused',
  muted_off:               'muted (Siren hours: Off)',
  outside_device_schedule: 'outside device schedule',
  no_device_configured:    'no device configured',
  'not recorded':          'not recorded (pre-dates logging)',
};

// A deliberate suppression ("suppressed: <why>") carries a full sentence meant to be
// read by a person — ~110 characters for scene memory. Too long for a table cell, so
// the cell gets the cause and the tooltip gets the sentence. Matched on the start of
// the string rather than the whole of it, since the sentence contains live numbers.
const SUPPRESS_CAUSE = [
  ['scene memory',   'scene memory (static spot)'],
  ['camera marked',  'night posture (nobody expected)'],
  ['fire on person', 'fire on a person'],
];

// 'suppressed: scene memory: cell 4,6 has alerted 5x…' → 'suppressed · scene memory…'
function shortReason(reason) {
  if (!reason) return null;
  if (REASON_LABEL[reason]) return REASON_LABEL[reason];
  const m = /^suppressed:\s*(.*)$/.exec(reason);
  if (!m) return reason;
  const found = SUPPRESS_CAUSE.find(([k]) => m[1].startsWith(k));
  return `suppressed · ${found ? found[1] : m[1]}`;
}

export default function DeviceAlertsReportPage() {
  const [token, setToken]   = useState('');
  const [rows, setRows]     = useState([]);
  const [cameras, setCameras] = useState([]);
  const [start, setStart]   = useState(ymd(new Date(Date.now() - 7 * 86400 * 1000)));
  const [end, setEnd]       = useState(ymd(new Date()));
  const [cam, setCam]       = useState('');
  const [showUnfired, setShowUnfired] = useState(false);
  const [loading, setLoading] = useState(false);
  // { kind: 'image' | 'video', url, title } — proof opens in-page, not a new tab
  const [preview, setPreview] = useState(null);

  useEffect(() => { getToken().then(t => setToken(t || '')); }, []);

  const imgUrl = f => f ? `${STREAM_API}/alerts/${encodeURIComponent(f)}/image${token ? `?token=${token}` : ''}` : null;
  const vidUrl = f => f ? `${STREAM_API}/alerts/${encodeURIComponent(f)}/video${token ? `?token=${token}` : ''}` : null;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const q = `start=${toStart(start)}&end=${toEnd(end)}${cam !== '' ? `&cam_idx=${cam}` : ''}`
              + (showUnfired ? '&include_unfired=true' : '');
      const r = await apiFetch(`/ui/alerts/device-report?${q}`);
      const d = r.ok ? await r.json() : { alerts: [], cameras: [] };
      setRows(d.alerts || []);
      setCameras(d.cameras || []);
    } finally { setLoading(false); }
  }, [start, end, cam, showUnfired]);

  useEffect(() => { load(); }, [load]);

  return (
    <div style={{ padding: 24, maxWidth: 1000, margin: '0 auto', color: 'var(--text)' }}>
      <h1 style={{ margin: '0 0 4px', fontSize: 24 }}>🚨 Device-Trigger Alerts</h1>
      <p style={{ color: 'var(--text-muted)', marginTop: 0 }}>Alerts that actually fired a device (siren / light / relay), with proof.</p>

      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end', margin: '16px 0' }}>
        <div><div style={lbl}>From</div><input type="date" value={start} onChange={e => setStart(e.target.value)} style={input} /></div>
        <div><div style={lbl}>To</div><input type="date" value={end} onChange={e => setEnd(e.target.value)} style={input} /></div>
        <div><div style={lbl}>Camera</div>
          <select value={cam} onChange={e => setCam(e.target.value)} style={input}>
            <option value="">All cameras</option>
            {cameras.map(c => <option key={c.idx} value={c.idx}>{c.name}</option>)}
          </select>
        </div>
        <button onClick={load} style={btn}>{loading ? 'Loading…' : 'Refresh'}</button>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, alignSelf: 'center', cursor: 'pointer' }}>
          <input type="checkbox" checked={showUnfired} onChange={e => setShowUnfired(e.target.checked)} />
          Include alerts that didn’t fire
        </label>
        <div style={{ marginLeft: 'auto', color: 'var(--text-muted)', fontSize: 13, alignSelf: 'center' }}>
          {showUnfired
            ? `${rows.filter(r => r.devices_fired).length} of ${rows.length} fired devices`
            : `${rows.length} alert${rows.length === 1 ? '' : 's'} fired devices`}
        </div>
      </div>

      <div style={{ overflowX: 'auto', border: '1px solid var(--border)', borderRadius: 10 }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14, minWidth: 720 }}>
          <thead><tr style={{ textAlign: 'left', color: 'var(--text-muted)', background: 'var(--surface)' }}>
            {['Time', 'Camera', 'Type', 'Devices', 'Verdict', 'Proof'].map(h => <th key={h} style={{ padding: '10px 12px', fontWeight: 600, whiteSpace: 'nowrap' }}>{h}</th>)}
          </tr></thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={6} style={{ padding: 24, textAlign: 'center', color: 'var(--text-muted)' }}>{loading ? 'Loading…' : 'No device-triggered alerts in this range.'}</td></tr>}
            {rows.map((r, i) => (
              <tr key={i} style={{ borderTop: '1px solid var(--border)' }}>
                <td style={td}>{fmt(r.ts)}</td>
                <td style={td}>{r.cam_name}</td>
                <td style={td}>{r.label || '—'}</td>
                <td style={td} title={r.note || (r.reason_detail ? JSON.stringify(r.reason_detail, null, 1) : '')}>
                  {r.devices_fired
                    ? <span style={{ color: 'var(--ok, #16a34a)', whiteSpace: 'nowrap' }}>✅ fired</span>
                    : <span style={{ color: 'var(--text-muted)' }}>🔇 {shortReason(r.reason) || 'not recorded'}</span>}
                  {/* A siren that fired despite a flagged spot — the release valve
                      working. Worth seeing in the report, not just journald. */}
                  {r.note && <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>
                    ⚠ nearly suppressed — hover for why
                  </div>}
                </td>
                <td style={td}>{r.verdict || <span style={{ color: 'var(--text-muted)' }}>—</span>}</td>
                <td style={td}>
                  {r.image
                    ? <img src={imgUrl(r.image)} alt="proof"
                        onClick={() => setPreview({ kind: 'image', url: imgUrl(r.image), title: `${r.cam_name} · ${fmt(r.ts)}` })}
                        style={{ height: 46, borderRadius: 6, border: '1px solid var(--border)', verticalAlign: 'middle', cursor: 'zoom-in' }} />
                    : <span style={{ color: 'var(--text-muted)' }}>no image</span>}
                  {r.clip && <button type="button" style={{ ...popupLink(link), marginLeft: 12 }}
                    onClick={() => setPreview({ kind: 'video', url: vidUrl(r.clip), title: `${r.cam_name} · ${fmt(r.ts)}` })}>Clip</button>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <MediaLightbox media={preview} onClose={() => setPreview(null)} />
    </div>
  );
}

const input = { padding: '8px 10px', background: 'var(--surface-alt)', color: 'var(--text)', border: '1px solid var(--border)', borderRadius: 6, fontSize: 14 };
const btn = { padding: '8px 14px', background: 'var(--surface-alt)', color: 'var(--text)', border: '1px solid var(--border)', borderRadius: 6, cursor: 'pointer', fontSize: 14 };
const lbl = { fontSize: 12, color: 'var(--text-muted)', marginBottom: 4 };
const td = { padding: '10px 12px', whiteSpace: 'nowrap' };
const link = { color: 'var(--accent)', textDecoration: 'underline', cursor: 'pointer' };
